import React, { useState, createContext, useContext, useRef, useEffect } from 'react';
import { FluxTab, FluxTabBar, FluxTabPane, FluxTabs } from '@meltwater/flux-web-components-react';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CategoryIcon from '@mui/icons-material/Category';
import RestoreIcon from '@mui/icons-material/Restore';
import Filter from './filter';
import Search from './search';
import CustomCat from './customcat';
import DeletedSearch from './deletedsearch';
import styles from './searchexports.module.css';
import LandingPageLayout from '../../components/LandingPageLayout';

// Create context for sharing data between search export components
const SearchDataContext = createContext();

export const SearchDataProvider = ({ children }) => {
    const [searchData, setSearchData] = useState(null);
    const [filterData, setFilterData] = useState(null);
    const [customCatData, setCustomCatData] = useState(null);
    const [deletedSearchData, setDeletedSearchData] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const contextValue = {
        searchData, 
        setSearchData,
        filterData, 
        setFilterData,
        customCatData, 
        setCustomCatData,
        deletedSearchData, 
        setDeletedSearchData,
        loading, 
        setLoading
    };

    return (
        <SearchDataContext.Provider value={contextValue}>
            {children}
        </SearchDataContext.Provider>
    );
};

export const useSearchData = () => useContext(SearchDataContext);

const Searchexports = () => {
    const [activeTab, setActiveTab] = useState(null);
    const tabsRef = useRef(null);

    const handleFeatureClick = (tabId) => {
        console.log("Setting active tab from feature click:", tabId);
        setActiveTab(tabId);
    };
    
    const featureCards = [
        {
            id: "searches",
            icon: <SearchIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Searches",
            description: "View and export search data including boolean queries, filters, and labels. Perform search validations and export results.",
            buttonText: "Go to Searches"
        },
        {
            id: "filters",
            icon: <FilterListIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Saved Filters",
            description: "Manage and export saved filters including source types, countries, languages, and custom configurations.",
            buttonText: "Go to Saved Filters"
        },
        {
            id: "CustomCat",
            icon: <CategoryIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Custom Categories",
            description: "View and export custom category configurations, including boolean queries and settings.",
            buttonText: "Go to Custom Categories"
        },
        {
            id: "deletedSearches",
            icon: <RestoreIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Deleted Searches",
            description: "Restore and manage deleted searches within the recovery period.",
            buttonText: "Go to Deleted Searches"
        }
    ];
    
    useEffect(() => {
        if (activeTab && tabsRef.current) {
            const allTabs = tabsRef.current.querySelectorAll('flux-tab');
            allTabs.forEach(tab => {
                tab.removeAttribute('active');
            });
            const tabElement = tabsRef.current.querySelector(`flux-tab[id="${activeTab}"]`);
            if (tabElement) {
                tabElement.setAttribute('active', 'true');
            }
        }
    }, [activeTab]);

    return (
        <SearchDataProvider>
            <div className={styles.divStyle}>
                {!activeTab ? (
                    <LandingPageLayout
                        title="Search & Filters Management"
                        description="Choose one of the options below to view or export search data."
                        features={featureCards}
                        onFeatureClick={handleFeatureClick}
                        onBackClick={() => window.location.href = '/'}
                        backButtonTooltip="Back to Dashboard"
                    />
                ) : (
                    <div className={styles.searchContainer}>
                        <FluxTabs ref={tabsRef}>
                            <FluxTabBar 
                                onFluxActivated={(event) => {
                                    setActiveTab(event.detail.tab.id);
                                    console.log("Tab bar activated:", event.detail.tab.id);
                                }}
                            >
                                <FluxTab id="searches" pane="searches">
                                    Searches
                                </FluxTab>
                                <FluxTab id="filters" pane="filters">
                                    Saved Filters
                                </FluxTab>
                                <FluxTab id="CustomCat" pane="CustomCat">
                                    Custom Categories
                                </FluxTab>
                                <FluxTab id="deletedSearches" pane="deletedSearches">
                                    Deleted Searches
                                </FluxTab>
                            </FluxTabBar>
                            <FluxTabPane name="searches" active={activeTab === "searches"}>
                                <div className={styles.divButtonTableContainer}>
                                    <Search load={activeTab === "searches"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                            <FluxTabPane name="filters" active={activeTab === "filters"}>
                                <div className={styles.divButtonTableContainer}>
                                    <Filter load={activeTab === "filters"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                            <FluxTabPane name="CustomCat" active={activeTab === "CustomCat"}>
                                <div className={styles.divButtonTableContainer}>
                                    <CustomCat load={activeTab === "CustomCat"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                            <FluxTabPane name="deletedSearches" active={activeTab === "deletedSearches"}>
                                <div className={styles.divButtonTableContainer}>
                                    <DeletedSearch load={activeTab === "deletedSearches"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                        </FluxTabs>
                    </div>
                )}
            </div>
        </SearchDataProvider>
    );
};

export default Searchexports;
